import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Building, Users, ShoppingBag, Plane, Heart, GraduationCap } from 'lucide-react';

const ForWho: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const useCases = [
    {
      icon: Building,
      title: 'Empresas',
      description: t('forWho.companies'),
      color: 'primary',
      examples: ['Atendimento ao Cliente', 'Informações Internas', 'Treinamento'],
    },
    {
      icon: ShoppingBag,
      title: 'Shoppings & Varejo',
      description: 'Totens informativos e assistentes de compra',
      color: 'secondary',
      examples: ['Localização de Lojas', 'Promoções', 'Horários'],
    },
    {
      icon: Plane,
      title: 'Aeroportos & Transporte',
      description: 'Informações de voos e orientação de passageiros',
      color: 'accent',
      examples: ['Status de Voos', 'Localização', 'Serviços'],
    },
    {
      icon: Heart,
      title: 'Hospitais & Saúde',
      description: 'Atendimento e orientação de pacientes',
      color: 'primary',
      examples: ['Agendamentos', 'Localização', 'Emergências'],
    },
    {
      icon: Users,
      title: 'Eventos',
      description: 'Assistentes para eventos e conferências',
      color: 'secondary',
      examples: ['Programação', 'Localização', 'Networking'],
    },
    {
      icon: GraduationCap,
      title: 'Instituições',
      description: 'Educacionais e governamentais',
      color: 'accent',
      examples: ['Informações', 'Serviços', 'Orientação'],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="for-who" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('forWho.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('forWho.subtitle')}
          </p>
        </motion.div>

        {/* Use Cases Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
        >
          {useCases.map((useCase, index) => {
            const IconComponent = useCase.icon;
            const colorClasses = {
              primary: 'bg-primary-100 text-primary-600 border-primary-200',
              secondary: 'bg-secondary-100 text-secondary-600 border-secondary-200',
              accent: 'bg-accent-100 text-accent-600 border-accent-200',
            };

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <div className={`w-16 h-16 rounded-xl ${colorClasses[useCase.color as keyof typeof colorClasses]} flex items-center justify-center mb-6`}>
                  <IconComponent size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {useCase.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {useCase.description}
                </p>
                <div className="space-y-2">
                  {useCase.examples.map((example, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                      <span className="text-sm text-gray-600">{example}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-12 text-center text-white"
        >
          <h3 className="text-3xl font-bold mb-6">
            Pronto para modernizar seu atendimento?
          </h3>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            O VoizX é a solução ideal para empresas que buscam oferecer uma experiência de atendimento moderna e eficiente.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-white text-primary-600 font-semibold rounded-full hover:shadow-lg transition-all duration-300"
            >
              Solicitar Demonstração
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-primary-600 transition-all duration-300"
            >
              Falar com Especialista
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ForWho; 